// =====================================================
// ROUTES: SANTÉ PSY ONLINE
// Date: 2025-10-02
// Description: Routes dédiées au système de télé-consultation
// =====================================================

import { lazy, Suspense } from 'react';
import { Route } from 'react-router-dom';

// Composant de chargement
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="relative">
      <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
    </div>
  </div>
);

// Pages publiques (lazy loading)
const PsyAuthPage = lazy(() => import('../pages/psy/PsyAuthPage'));
const PractitionerList = lazy(() => import('../pages/psy/PractitionerList'));
const PractitionerProfile = lazy(() => import('../pages/psy/PractitionerProfile'));
const BookAppointment = lazy(() => import('../pages/psy/BookAppointment'));
const TriageQuestionnaire = lazy(() => import('../pages/psy/TriageQuestionnaire'));
const EmergencyProtocol = lazy(() => import('../pages/psy/EmergencyProtocol'));

// Pages patient
const PatientDashboard = lazy(() => import('../pages/psy/PatientDashboard'));
const AppointmentDetails = lazy(() => import('../pages/psy/AppointmentDetails'));
const WaitingRoom = lazy(() => import('../pages/psy/WaitingRoom'));
const ConsultationRoom = lazy(() => import('../pages/psy/ConsultationRoom'));

// Pages praticien
const PractitionerOnboarding = lazy(() => import('../pages/psy/PractitionerOnboarding'));
const PractitionerDashboard = lazy(() => import('../pages/psy/PractitionerDashboard'));
const PractitionerAgenda = lazy(() => import('../pages/psy/PractitionerAgenda'));
const PatientFile = lazy(() => import('../pages/psy/PatientFile'));

// Pages admin
const AdminPractitionersList = lazy(() => import('../pages/admin/psy/AdminPractitionersList'));
const AdminPatientsList = lazy(() => import('../pages/admin/psy/AdminPatientsList'));
const AdminPractitionerVerification = lazy(() => import('../pages/admin/psy/AdminPractitionerVerification'));
const AdminPsyAppointments = lazy(() => import('../pages/admin/psy/AdminPsyAppointments'));
const AdminPsyTriage = lazy(() => import('../pages/admin/psy/AdminPsyTriage'));

// =====================================================
// ROUTES PUBLIQUES
// =====================================================
export const psyPublicRoutes = (
  <>
    <Route path="/psy/auth" element={<Suspense fallback={<LoadingFallback />}><PsyAuthPage /></Suspense>} />
    <Route path="/psy/praticiens" element={<Suspense fallback={<LoadingFallback />}><PractitionerList /></Suspense>} />
    <Route path="/psy/praticiens/:id" element={<Suspense fallback={<LoadingFallback />}><PractitionerProfile /></Suspense>} />
    <Route path="/psy/reserver/:practitionerId" element={<Suspense fallback={<LoadingFallback />}><BookAppointment /></Suspense>} />
    <Route path="/psy/triage" element={<Suspense fallback={<LoadingFallback />}><TriageQuestionnaire /></Suspense>} />
    <Route path="/psy/urgence" element={<Suspense fallback={<LoadingFallback />}><EmergencyProtocol /></Suspense>} />
  </>
);

// =====================================================
// ROUTES PATIENT (Protégées)
// =====================================================
export const psyPatientRoutes = (
  <>
    <Route path="/psy/dashboard" element={<Suspense fallback={<LoadingFallback />}><PatientDashboard /></Suspense>} />
    <Route path="/psy/rdv/:id" element={<Suspense fallback={<LoadingFallback />}><AppointmentDetails /></Suspense>} />
    <Route path="/psy/salle-attente/:id" element={<Suspense fallback={<LoadingFallback />}><WaitingRoom /></Suspense>} />
    <Route path="/psy/consultation/:id" element={<Suspense fallback={<LoadingFallback />}><ConsultationRoom /></Suspense>} />
  </>
);

// =====================================================
// ROUTES PRATICIEN (Protégées)
// =====================================================
export const psyPractitionerRoutes = (
  <>
    <Route path="/psy/praticien/inscription" element={<Suspense fallback={<LoadingFallback />}><PractitionerOnboarding /></Suspense>} />
    <Route path="/psy/dashboard/praticien" element={<Suspense fallback={<LoadingFallback />}><PractitionerDashboard /></Suspense>} />
    <Route path="/psy/dashboard/praticien/agenda" element={<Suspense fallback={<LoadingFallback />}><PractitionerAgenda /></Suspense>} />
    <Route path="/psy/dashboard/praticien/patients/:patientId" element={<Suspense fallback={<LoadingFallback />}><PatientFile /></Suspense>} />
  </>
);

// =====================================================
// ROUTES ADMIN (Protégées)
// =====================================================
export const psyAdminRoutes = (
  <>
    <Route path="/admin/psy/praticiens" element={<Suspense fallback={<LoadingFallback />}><AdminPractitionersList /></Suspense>} />
    <Route path="/admin/psy/patients" element={<Suspense fallback={<LoadingFallback />}><AdminPatientsList /></Suspense>} />
    <Route path="/admin/psy/praticiens/:id/verifier" element={<Suspense fallback={<LoadingFallback />}><AdminPractitionerVerification /></Suspense>} />
    <Route path="/admin/psy/appointments" element={<Suspense fallback={<LoadingFallback />}><AdminPsyAppointments /></Suspense>} />
    <Route path="/admin/psy/triage" element={<Suspense fallback={<LoadingFallback />}><AdminPsyTriage /></Suspense>} />
  </>
);
